import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Presentational primitives. Everything is a plain function component over the
 * CSS in styles/app.css so pages stay declarative and no component library
 * leaks styling decisions into business code.
 */
import { useEffect, useRef, useState } from "react";
/* ------------------------------------------------------------------ status */
const OK = new Set([
    "completed",
    "fulfilled",
    "passed",
    "approved",
    "published",
    "active",
    "succeeded",
    "confirmed",
    "captured",
    "delivered",
    "paid",
    "accepted",
    "released",
    "verified",
    "balanced",
]);
const WARN = new Set([
    "pending",
    "requires_action",
    "in_production",
    "partially_fulfilled",
    "draft",
    "submitted",
    "reserved",
    "in_transit",
    "awaiting",
    "paused",
    "review",
    "manual_review",
    "planned",
    "issued",
    "queued",
    "received",
]);
const DANGER = new Set([
    "failed",
    "rejected",
    "cancelled",
    "canceled",
    "dead",
    "expired",
    "declined",
    "blocked",
    "nonconforming",
    "disputed",
]);
const INFO = new Set(["running", "dispatched", "shipped", "processing", "candidate", "confirmed_pending"]);
export function toneForStatus(status) {
    const s = String(status ?? "").toLowerCase();
    if (!s)
        return "neutral";
    if (OK.has(s))
        return "ok";
    if (DANGER.has(s))
        return "danger";
    if (WARN.has(s))
        return "warn";
    if (INFO.has(s))
        return "info";
    return "neutral";
}
export function Pill({ tone = "neutral", children, dot = false }) {
    return (_jsxs("span", { className: `pill pill--${tone}`, children: [dot ? _jsx("i", { className: "pill__dot" }) : null, children] }));
}
export function StatusPill({ value, dot = true }) {
    const s = String(value ?? "—");
    return (_jsx(Pill, { tone: toneForStatus(s), dot: dot, children: s }));
}
export function PriorityPill({ value }) {
    const tone = value === "P0" ? "danger" : value === "P1" ? "warn" : "neutral";
    return _jsx(Pill, { tone: tone, children: value });
}
/* -------------------------------------------------------------------- page */
export function Page({ title, titleEn, subtitle, actions, children, meta, }) {
    return (_jsxs("div", { className: "page", children: [_jsxs("div", { className: "page__head", children: [_jsxs("div", { style: { minWidth: 0 }, children: [_jsxs("div", { className: "page__title", children: [_jsx("h1", { children: title }), titleEn ? _jsx("span", { className: "section-label", children: titleEn }) : null, meta] }), subtitle ? _jsx("p", { className: "page__sub", children: subtitle }) : null] }), actions ? _jsx("div", { className: "page__actions", children: actions }) : null] }), children] }));
}
export function Card({ title, actions, children, flush = false, sub, }) {
    return (_jsxs("section", { className: "card", children: [title || actions ? (_jsxs("header", { className: "card__head", children: [_jsxs("div", { style: { minWidth: 0 }, children: [typeof title === "string" ? _jsx("h2", { children: title }) : title, sub ? _jsx("div", { className: "muted", style: { fontSize: 12, marginTop: 2 }, children: sub }) : null] }), actions ? _jsx("div", { className: "row", style: { gap: 8 }, children: actions }) : null] })) : null, _jsx("div", { className: flush ? "card__body--flush" : "card__body", children: children })] }));
}
export function Metric({ label, value, hint }) {
    return (_jsxs("div", { className: "card metric", children: [_jsx("div", { className: "metric__label", children: label }), _jsx("div", { className: "metric__value", children: value }), hint ? _jsx("div", { className: "metric__hint", children: hint }) : null] }));
}
export function Notice({ tone = "info", children }) {
    const cls = tone === "info" ? "" : ` notice--${tone}`;
    return _jsx("div", { className: `notice${cls}`, children: children });
}
export function Empty({ title, detail, action }) {
    return (_jsxs("div", { className: "empty", children: [_jsx("div", { className: "empty__icon", "aria-hidden": true, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.8", children: [_jsx("rect", { x: "3", y: "5", width: "18", height: "14", rx: "2" }), _jsx("path", { d: "M3 10h18M8 15h5", strokeLinecap: "round" })] }) }), _jsx("strong", { children: title }), detail ? _jsx("p", { children: detail }) : null, action] }));
}
export function Skeleton({ h = 14, w = "100%", style }) {
    return _jsx("div", { className: "skeleton", style: { height: h, width: w, ...style } });
}
export function Loading({ rows = 4 }) {
    return (_jsx("div", { className: "stack stack--sm", style: { padding: 16 }, children: Array.from({ length: rows }).map((_, i) => (_jsx(Skeleton, { h: 12, w: `${100 - i * 11}%` }, i))) }));
}
export function KV({ items }) {
    return (_jsx("dl", { className: "kv", children: items.map(([k, v]) => (_jsxs("div", { style: { display: "contents" }, children: [_jsx("dt", { children: k }), _jsx("dd", { children: v })] }, k))) }));
}
/* --------------------------------------------------------------------- api */
const MEMBER_LABEL = {
    commerce: "commerce",
    rnd: "rnd",
    scm: "scm",
    mfg: "mfg",
    platform: "platform",
};
export function ApiTag({ api }) {
    const cls = `tag tag--method-${api.method.toLowerCase()}`;
    return (_jsxs("span", { className: cls, title: `${api.state === "live" ? "已实现" : "仅契约"} · /api/v1/${api.member}${api.path}`, children: [api.method, " /", MEMBER_LABEL[api.member] ?? api.member, api.path, api.state === "contract" ? " · spec" : ""] }));
}
export function ApiList({ apis }) {
    if (!apis.length)
        return _jsx("span", { className: "muted", children: "\u2014" });
    return (_jsx("div", { className: "row", style: { gap: 6 }, children: apis.map((a, i) => (_jsx(ApiTag, { api: a }, `${a.member}${a.method}${a.path}${i}`))) }));
}
/* -------------------------------------------------------------------- async */
export function ErrorNotice({ error, onRetry }) {
    return (_jsx(Notice, { tone: "error", children: _jsxs("div", { children: [_jsx("strong", { children: error.problem.title || "请求失败" }), error.problem.detail ? _jsx("div", { style: { marginTop: 2 }, children: error.problem.detail }) : null, _jsxs("div", { className: "row", style: { marginTop: 8, gap: 8 }, children: [error.problem.code ? _jsx("span", { className: "tag", children: error.problem.code }) : null, error.status ? _jsxs("span", { className: "tag", children: ["HTTP ", error.status] }) : null, error.problem.correlation_id ? _jsxs("span", { className: "tag", children: ["cid ", error.problem.correlation_id.slice(0, 8)] }) : null, onRetry ? (_jsx("button", { type: "button", className: "btn btn--sm", onClick: onRetry, children: "\u91CD\u8BD5" })) : null] })] }) }));
}
export function Async({ state, children, skeletonRows = 4, empty, }) {
    if (state.error)
        return _jsx(ErrorNotice, { error: state.error, onRetry: state.reload });
    if (state.data === null) {
        if (state.loading || state.initial)
            return _jsx(Loading, { rows: skeletonRows });
        return _jsx(_Fragment, { children: empty ?? _jsx(Empty, { title: "\u6682\u65E0\u6570\u636E" }) });
    }
    return _jsx(_Fragment, { children: children(state.data) });
}
/* --------------------------------------------------------------------- form */
export function Field({ label, hint, error, children, className, }) {
    return (_jsxs("div", { className: `field${className ? ` ${className}` : ""}`, children: [_jsx("label", { children: label }), children, error ? _jsx("span", { className: "field__hint", style: { color: "var(--vnmade-danger-700)" }, children: error }) : null, hint ? _jsx("span", { className: "field__hint", children: hint }) : null] }));
}
/* ------------------------------------------------------------------- drawer */
export function Drawer({ open, title, onClose, children, width = 420, }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open)
        return null;
    return (_jsxs("div", { className: "drawer", style: { gridTemplateColumns: `minmax(0, 1fr) ${width}px` }, children: [_jsx("div", { className: "drawer__scrim", onClick: onClose, role: "presentation" }), _jsxs("div", { className: "drawer__panel", ref: ref, role: "dialog", "aria-modal": "true", children: [_jsxs("div", { className: "drawer__head", children: [_jsx("strong", { style: { fontSize: 14 }, children: title }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: onClose, children: "\u5173\u95ED" })] }), _jsx("div", { className: "drawer__body", children: children })] })] }));
}
/* -------------------------------------------------------------------- misc */
export function CopyButton({ value, label = "复制" }) {
    const [done, setDone] = useState(false);
    return (_jsx("button", { type: "button", className: "btn btn--ghost btn--sm", onClick: async () => {
            try {
                await navigator.clipboard.writeText(value);
                setDone(true);
                window.setTimeout(() => setDone(false), 1200);
            }
            catch {
                /* clipboard blocked — no-op */
            }
        }, children: done ? "已复制" : label }));
}
export function JsonBlock({ value }) {
    return _jsx("pre", { className: "codeblock", children: JSON.stringify(value, null, 2) });
}
export function Bars({ data }) {
    const max = Math.max(1, ...data.map((d) => d.value));
    return (_jsx("div", { className: "stack stack--sm", children: data.map((d) => (_jsxs("div", { children: [_jsxs("div", { className: "row row--between", style: { marginBottom: 3 }, children: [_jsx("span", { style: { fontSize: 12, color: "var(--vnmade-ink-500)" }, children: d.label }), _jsx("span", { className: "num", style: { fontSize: 12, fontWeight: 600, color: "var(--vnmade-ink-800)" }, children: d.value })] }), _jsx("div", { className: "bar", children: _jsx("div", { className: `bar__fill${d.tone === "warn" ? " bar__fill--warn" : d.tone === "ok" ? " bar__fill--ok" : ""}`, style: { width: `${Math.round((d.value / max) * 100)}%` } }) })] }, d.label))) }));
}
